import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input } from '@cloudflare/kumo/components/input';
import { Select } from '@cloudflare/kumo/components/select';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import { Tooltip } from '@cloudflare/kumo/components/tooltip';
import {
  DialogRoot,
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@cloudflare/kumo/components/dialog';
import {
  MagnifyingGlass,
  ArrowCounterClockwise,
  DownloadSimple,
  Question,
  ArrowsClockwise,
  X,
} from '@phosphor-icons/react';
import YearSelector, { SupportedYear } from '@/components/YearSelector';
import { api } from '@/lib/api';
import {
  ADMIN_STATUS_LABEL,
  ADMIN_STATUS_VARIANT,
  ADMIN_STATUS_DESCRIPTION,
  EXHIBITOR_STATUS_ORDER,
} from '@nt-tokyo/shared';

// 一覧 API が返す料金内訳 (2026 のみ。使うフィールドだけ持つ)。
// exhibitors.totalFee は final_*_fee の override も少額スポンサー額も含まないため、
// 金額表示にはこちらを使う (支払い管理画面と同じ値になる)。
interface BillingBreakdown {
  venue: number;
  equipment: number;
  power: number;
  sponsor: number;
  sponsorUnits: number;
  grandTotal: number;
  finalUsed: boolean;
  isFree: boolean;
}

interface ExhibitorRow {
  id: string;
  applicationNumber: number;
  email: string;
  realName: string;       // 本名 (機密 / 公開しない)
  exhibitorName: string;  // 出展者名 (公開・掲示用)
  type: string;
  status: string;
  eventYear: number;
  totalFee: number;
  // 2026 のみ。2025 以前は null なので totalFee にフォールバックする。
  billing: BillingBreakdown | null;
  // 実際に決済された額 (payments テーブル)。paid でも payments 行が無いことがある
  // (全額無料で Stripe をスキップ / 銀行振込等で運営が手動で paid にした) ので null 可。
  paidAmount: number | null;
  boothNumber: string | null;
  // 2026 出展者は exhibitor_application_2026 から merge される (それ以前は null)。
  tableCount: number | null;
  chairCount: number | null;
  powerWatts: number | null;
  // sponsorUnits は意図的に持たない。申込時の仮値でしかなく後追いの追加支援 (addon) を
  // 含まないため、一覧に出すと実績と誤読される。少額スポンサーの実績は /sponsorships 参照。
  // 隣接配置希望グループ (未所属なら null / 空配列)
  adjacencyGroupId: string | null;
  adjacencyGroupCode: string | null;
  adjacencyGroupMembers: { applicationNumber: number; exhibitorName: string }[];
}

interface ListResponse {
  data: ExhibitorRow[];
  // pagination は API レスポンスでは meta.pagination に入る (shared/apiResponse.ts createSuccessResponse)
  meta?: {
    pagination?: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      limit: number;
    };
  };
}

// admin は全件を1画面で確認したいので、会場規模 (数百組) を十分カバーする大きな値にして
// 実質ページネーションを無効化する。ページャ UI は撤去し、常に 1 ページに全件を表示する。
const PAGE_LIMIT = 1000;
const DEFAULT_YEAR: SupportedYear = 2026; // 現役年度をデフォルト表示

// 一括変更のうち、ステータスを書き換えるだけでは済まない (来場者向けの公開一覧が動く)
// ものは確認ダイアログで個別に警告する。副作用の実体は API 側の bulk-update-status。
const BULK_STATUS_WARNING: Record<string, string> = {
  paid: '入金の確認は行われません。支払済にすると支払日時と支払い方法 (admin_override) が記録され、来場者向けの公開出展者一覧にただちに掲載されます。',
  cancelled: 'キャンセルにすると、来場者向けの公開出展者一覧から削除されます。',
};

const yenMark = (n: number) => `¥${(n ?? 0).toLocaleString('ja-JP')}`;

// 合計金額セル。支払い管理画面 (PaymentsPage) と同じ優先順位で出す。
//   1. paid かつ payments 行がある → 実際に決済された額
//   2. billing がある (2026)       → 現在の申込内容からの再計算値
//   3. どちらも無い (2025 以前)     → exhibitors.totalFee
// exhibitors.totalFee は final_*_fee の override も少額スポンサー額も含まないため、
// これを 2026 の行に出すと支払い管理画面と金額が食い違う。
function feeCell(row: ExhibitorRow) {
  if (row.status === 'paid' && row.paidAmount !== null) {
    return <span title="実際に決済された額 (payments)">{yenMark(row.paidAmount)}</span>;
  }
  const b = row.billing;
  if (!b) return yenMark(row.totalFee ?? 0);

  const detail =
    `会場費 ${yenMark(b.venue)} + 備品費 ${yenMark(b.equipment)} + 電源費 ${yenMark(b.power)}` +
    (b.sponsorUnits > 0 ? ` + 少額スポンサー ${yenMark(b.sponsor)} (${b.sponsorUnits} 口)` : '') +
    (row.status === 'paid' ? '\n決済記録が無いため、現在の申込内容から再計算した参考値です' : '');

  return (
    <span title={detail}>
      {yenMark(b.grandTotal)}
      {b.isFree && (
        <Badge variant="outline" className="ml-2">
          免除
        </Badge>
      )}
      {!b.isFree && b.finalUsed && (
        <Badge variant="outline" className="ml-2">
          調整済
        </Badge>
      )}
      {row.status === 'paid' && (
        <Badge variant="outline" className="ml-2">
          参考値
        </Badge>
      )}
    </span>
  );
}

// 隣接配置グループの色分け用パレット。Kumo の Badge variant には色相トークンが7色しか
// 無く、本番で既に11グループ運用されている実態に足りないため、Tailwind のベースパレットから
// 色相を色相環にほぼ均等に配置して12色に拡張した。全て背景色に対する白文字のコントラスト比が
// WCAG AA (4.5:1) 以上になるシェード (600/700) のみを採用している (このアプリはダークモード
// 未対応のため固定色で問題ない)。
const ADJACENCY_GROUP_COLORS = [
  { bg: 'bg-red-600', text: 'text-white' },
  { bg: 'bg-orange-700', text: 'text-white' },
  { bg: 'bg-amber-700', text: 'text-white' },
  { bg: 'bg-lime-700', text: 'text-white' },
  { bg: 'bg-emerald-700', text: 'text-white' },
  { bg: 'bg-teal-700', text: 'text-white' },
  { bg: 'bg-cyan-700', text: 'text-white' },
  { bg: 'bg-blue-600', text: 'text-white' },
  { bg: 'bg-indigo-600', text: 'text-white' },
  { bg: 'bg-purple-600', text: 'text-white' },
  { bg: 'bg-fuchsia-600', text: 'text-white' },
  { bg: 'bg-pink-600', text: 'text-white' },
] as const;

// グループごとの色を、出展者番号順に初めて登場した順番でパレットから割り当てる。
// ハッシュ mod だとパレット数 (7色) 程度のグループ数で高確率に衝突してしまうため、
// 画面内で近くに表示される (=同時に見比べる可能性が高い) グループから優先的に
// 別の色を割り当てる方式にしている。パレットを使い切った場合、色をローテーションして
// 使い回すと別グループを誤認させるので、それ以降は色なし (無色バッジ) にフォールバックする。
function useAdjacencyGroupColors(rows: ExhibitorRow[]): Map<string, (typeof ADJACENCY_GROUP_COLORS)[number]> {
  return useMemo(() => {
    const colorByGroupId = new Map<string, (typeof ADJACENCY_GROUP_COLORS)[number]>();
    for (const row of rows) {
      if (!row.adjacencyGroupId || colorByGroupId.has(row.adjacencyGroupId)) continue;
      if (colorByGroupId.size >= ADJACENCY_GROUP_COLORS.length) continue;
      colorByGroupId.set(row.adjacencyGroupId, ADJACENCY_GROUP_COLORS[colorByGroupId.size]);
    }
    return colorByGroupId;
  }, [rows]);
}

// ステータスラベル / variant は @nt-tokyo/shared の labels.ts に集約。
// admin / public-site で表記がぶれないように共通参照する。

export default function ExhibitorsPage() {
  const [year, setYear] = useState<SupportedYear>(DEFAULT_YEAR);
  const [rows, setRows] = useState<ExhibitorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const adjacencyGroupColors = useAdjacencyGroupColors(rows);

  // 一括ステータス変更 (2025 admin の一覧直書き一括操作を踏襲)。
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('');
  // プレビュー時点の対象 ID / ステータス / 理由をスナップショットとして保持する。
  // ダイアログ表示中に selectedIds / bulkStatus / bulkReason が変わっても、
  // 確認ダイアログの表示内容と実際に適用される操作を一致させるため。
  const [bulkPreviewing, setBulkPreviewing] = useState<{
    exhibitorIds: string[];
    status: string;
    reason: string;
    eligibleCount: number;
    skippedCount: number;
  } | null>(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkInfo, setBulkInfo] = useState<string | null>(null);
  // キャンセル済みは既定で非表示。必要なときだけ明示的に出す。
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const fetchRows = async (
    opts: {
      page?: number;
      search?: string;
      year?: SupportedYear;
      includeCancelled?: boolean;
    } = {}
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(opts.page ?? page));
      params.set('limit', String(PAGE_LIMIT));
      params.set('year', String(opts.year ?? year));
      // キャンセル済みは既定で出さない。一覧に混ざっていると一括操作でうっかり
      // 対象に含めてしまい、キャンセル済みの出展者を支払い待ちに戻してしまう事故が起きる。
      if (opts.includeCancelled ?? includeCancelled) params.set('includeCancelled', 'true');
      if (opts.search ?? search) params.set('search', opts.search ?? search);
      const res = await api.get<ListResponse>(`/api/admin/exhibitors?${params.toString()}`);
      setRows(res.data.data ?? []);
      setTotal(res.data.meta?.pagination?.totalItems ?? res.data.data?.length ?? 0);
      // 一覧が入れ替わると選択の対象がずれるので毎回クリアする。
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      setError('出展者一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))
    );
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openBulkPreview = async () => {
    if (selectedIds.size === 0 || !bulkStatus) return;
    setError(null);
    // 以降 selectedIds / bulkStatus / bulkReason が変わっても操作内容がぶれないよう、
    // この時点の値をスナップショットして dryRun にも apply にも同じ値を使う。
    const exhibitorIds = Array.from(selectedIds);
    const status = bulkStatus;
    const reason = bulkReason;
    try {
      const res = await api.post('/api/admin/exhibitors/bulk-update-status', {
        exhibitorIds,
        status,
        reason,
        dryRun: true,
      });
      const data = (res.data?.data ?? res.data) as { eligibleCount: number; skippedCount: number };
      setBulkPreviewing({
        exhibitorIds,
        status,
        reason,
        eligibleCount: data.eligibleCount,
        skippedCount: data.skippedCount,
      });
    } catch (err) {
      console.error(err);
      setError('一括変更のプレビューに失敗しました');
    }
  };

  const confirmBulkApply = async () => {
    if (!bulkPreviewing) return;
    const { exhibitorIds, status, reason } = bulkPreviewing;
    setBulkApplying(true);
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/bulk-update-status', {
        exhibitorIds,
        status,
        reason,
        dryRun: false,
      });
      const data = (res.data?.data ?? res.data) as {
        updated: number;
        skipped?: Array<{ exhibitorId: string; reason: string }>;
      };
      setBulkInfo(
        `${data.updated} 件のステータスを「${ADMIN_STATUS_LABEL[status as keyof typeof ADMIN_STATUS_LABEL] ?? status}」に変更しました。${
          data.skipped?.length ? ` / スキップ ${data.skipped.length} 件` : ''
        }`
      );
      setBulkPreviewing(null);
      setBulkStatus('');
      setBulkReason('');
      await fetchRows({ page, search, year });
    } catch (err) {
      console.error(err);
      setError('一括変更に失敗しました');
    } finally {
      setBulkApplying(false);
    }
  };

  useEffect(() => {
    fetchRows({ page, search, year });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, year]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
    fetchRows({ page: 1, search: searchInput.trim(), year });
  };

  // CSV エクスポート: 内容確認は紙運用なので、年度の全件 + 年度別詳細を flatten で落とす。
  // 個人情報を含むのでサーバ側で admin_actions にも記録される。
  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.get<Blob>(`/api/admin/exhibitors/export?year=${year}`, {
        responseType: 'blob',
      });
      const blob = res.data;
      const cd = res.headers['content-disposition'] as string | undefined;
      const match = cd?.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? `nt-tokyo-exhibitors-${year}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('CSV エクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-kumo-default">出展者一覧</h1>
        <div className="flex items-center gap-2">
          <YearSelector
            value={year}
            onChange={(y) => {
              setYear(y);
              setPage(1);
            }}
          />
          <Button
            variant="ghost"
            icon={ArrowCounterClockwise}
            loading={loading}
            onClick={() => fetchRows({ page, search, year })}
          >
            再読込
          </Button>
          <Button
            variant="secondary"
            icon={DownloadSimple}
            loading={exporting}
            onClick={handleExport}
          >
            CSV
          </Button>
        </div>
      </div>

      <Surface className="p-4">
        <form onSubmit={handleSearch} className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="名前 / メール / 出展タイトルで検索">
              <Input
                placeholder="例: 山田 / yamada@example.com / 〇〇プロジェクト"
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
              />
            </Field>
          </div>
          <Button type="submit" variant="primary" icon={MagnifyingGlass}>
            検索
          </Button>
        </form>
        {/* キャンセル済みは既定で出さない (一括操作で誤って対象に含めないため)。
            表示したいときだけ明示的に切り替える。 */}
        <label className="mt-3 flex items-center gap-2 text-sm text-kumo-default">
          <input
            type="checkbox"
            data-testid="include-cancelled-toggle"
            checked={includeCancelled}
            onChange={(e) => {
              const next = e.currentTarget.checked;
              setIncludeCancelled(next);
              setPage(1);
              fetchRows({ page: 1, search, year, includeCancelled: next });
            }}
          />
          キャンセル済みも表示する
        </label>
      </Surface>

      {error ? <Banner variant="error">{error}</Banner> : null}
      {bulkInfo ? <Banner variant="default">{bulkInfo}</Banner> : null}

      {selectedIds.size > 0 && (
        <Surface className="flex flex-wrap items-end gap-3 p-4">
          <span className="text-sm font-medium text-kumo-default">
            {selectedIds.size} 件選択中
          </span>
          <div className="w-56" data-testid="bulk-status-select">
            <Field label="変更後のステータス">
              <Select<string>
                value={bulkStatus}
                onValueChange={(v) => setBulkStatus(v ?? '')}
                disabled={bulkPreviewing !== null}
                renderValue={(v) =>
                  v ? ADMIN_STATUS_LABEL[v as keyof typeof ADMIN_STATUS_LABEL] ?? v : '選択してください'
                }
              >
                {EXHIBITOR_STATUS_ORDER.map((s) => (
                  <Select.Option key={s} value={s}>
                    {ADMIN_STATUS_LABEL[s]}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="flex-1 min-w-[12rem]">
            <Field label="理由 (admin_actions に記録・任意)">
              <Input
                data-testid="bulk-reason-input"
                placeholder="例: 配置・料金確定済のため一括で支払い待ちへ"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.currentTarget.value)}
                disabled={bulkPreviewing !== null}
              />
            </Field>
          </div>
          <Button
            variant="primary"
            icon={ArrowsClockwise}
            disabled={!bulkStatus || bulkPreviewing !== null}
            onClick={openBulkPreview}
          >
            一括変更
          </Button>
          <Button
            variant="ghost"
            icon={X}
            disabled={bulkPreviewing !== null}
            onClick={() => setSelectedIds(new Set())}
          >
            選択解除
          </Button>
        </Surface>
      )}

      <Surface>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <Empty
            title={`${year} 年度の出展者は見つかりません`}
            description={search ? `"${search}" に一致する出展者がいません` : '該当年度の出展者がまだいません'}
          />
        ) : (
          <Table layout="auto">
            <Table.Header>
              <Table.Row>
                <Table.Head>
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onChange={toggleAll}
                    aria-label="すべて選択"
                  />
                </Table.Head>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者名 / 本名</Table.Head>
                <Table.Head>メール</Table.Head>
                <Table.Head>種別</Table.Head>
                <Table.Head>
                  <span className="inline-flex items-center gap-1">
                    ステータス
                    <Tooltip
                      content={
                        <div className="flex max-w-xs flex-col gap-1 text-xs leading-snug">
                          {EXHIBITOR_STATUS_ORDER.map((s) => (
                            <div key={s}>
                              <span className="font-semibold">{ADMIN_STATUS_LABEL[s]}</span>
                              <span className="text-kumo-muted"> — {ADMIN_STATUS_DESCRIPTION[s]}</span>
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <button
                        type="button"
                        aria-label="ステータスの凡例を表示"
                        className="inline-flex items-center justify-center text-kumo-muted hover:text-kumo-default focus:outline-none"
                      >
                        <Question size={14} weight="bold" />
                      </button>
                    </Tooltip>
                  </span>
                </Table.Head>
                <Table.Head>机 (本)</Table.Head>
                <Table.Head>椅子 (脚)</Table.Head>
                <Table.Head>電源 (VA)</Table.Head>
                <Table.Head>ブース</Table.Head>
                <Table.Head>隣接配置</Table.Head>
                <Table.Head className="text-right">合計金額 (税込)</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      aria-label={`#${row.applicationNumber} を選択`}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Link to={`/exhibitors/${row.id}`} className="text-kumo-link hover:underline">
                      {row.applicationNumber}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">本名: {row.realName}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{row.email}</Table.Cell>
                  <Table.Cell>{typeLabel(row.type)}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={ADMIN_STATUS_VARIANT[row.status as keyof typeof ADMIN_STATUS_VARIANT] ?? 'outline'}>
                      {ADMIN_STATUS_LABEL[row.status as keyof typeof ADMIN_STATUS_LABEL] ?? row.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{row.tableCount ?? '—'}</Table.Cell>
                  <Table.Cell>{row.chairCount ?? '—'}</Table.Cell>
                  <Table.Cell>{row.powerWatts === null ? '—' : row.powerWatts === 0 ? 'なし' : row.powerWatts}</Table.Cell>
                  <Table.Cell>{row.boothNumber ?? '—'}</Table.Cell>
                  <Table.Cell>
                    {row.adjacencyGroupCode && row.adjacencyGroupId ? (
                      (() => {
                        // パレットを使い切った分は色なし (アウトライン) にフォールバックする。
                        // 既存の色を使い回すと別グループと誤認されるため、ローテーションはしない。
                        const color = adjacencyGroupColors.get(row.adjacencyGroupId);
                        const badgeClassName = color
                          ? `${color.bg} ${color.text}`
                          : 'border border-kumo-fill bg-transparent text-kumo-default';
                        return (
                          <Tooltip
                            delay={0}
                            content={
                              <div className="flex max-w-xs flex-col gap-1 text-xs leading-snug">
                                <span className="font-semibold">同一グループの出展者</span>
                                {row.adjacencyGroupMembers.map((m) => (
                                  <div key={m.applicationNumber}>
                                    #{m.applicationNumber} {m.exhibitorName}
                                  </div>
                                ))}
                              </div>
                            }
                          >
                            <Link
                              to={`/adjacency-groups#group-${row.adjacencyGroupId}`}
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium hover:opacity-80 ${badgeClassName}`}
                            >
                              {row.adjacencyGroupCode}
                            </Link>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      <span className="text-kumo-muted">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-right">{feeCell(row)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <div className="flex items-center justify-between">
        <span className="text-xs text-kumo-muted">全 {total} 件を表示</span>
      </div>

      <DialogRoot
        role="alertdialog"
        open={bulkPreviewing !== null}
        onOpenChange={(open) => {
          if (!open) setBulkPreviewing(null);
        }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>ステータスを一括変更しますか?</DialogTitle>
          <DialogDescription>
            {bulkPreviewing && (
              <>
                選択した <strong>{bulkPreviewing.eligibleCount}</strong> 件を「
                {ADMIN_STATUS_LABEL[bulkPreviewing.status as keyof typeof ADMIN_STATUS_LABEL] ??
                  bulkPreviewing.status}
                」に変更します。メールは送信されません。
                {BULK_STATUS_WARNING[bulkPreviewing.status] && (
                  <span className="mt-2 block rounded border border-kumo-warning bg-kumo-warning-tint p-2 text-sm text-kumo-default">
                    {BULK_STATUS_WARNING[bulkPreviewing.status]}
                  </span>
                )}
                {bulkPreviewing.skippedCount > 0 && (
                  <>
                    <br />
                    当年度でない、またはすでに同じステータスの{' '}
                    {bulkPreviewing.skippedCount} 件はスキップされます。
                  </>
                )}
              </>
            )}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose
              render={(p) => (
                <Button {...p} variant="secondary">
                  キャンセル
                </Button>
              )}
            />
            <Button onClick={confirmBulkApply} loading={bulkApplying} icon={ArrowsClockwise}>
              変更する
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function typeLabel(t: string): string {
  switch (t) {
    case 'general':
      return '一般';
    case 'student-university':
      return '大学生';
    case 'student-high-school':
      return '高校生以下';
    case 'corporate':
      return '企業';
    default:
      return t;
  }
}
